import { useDeleteExpeditionBo } from "@/api/expedition.api";
import { useCreateNoteBo } from "@/api/notes.api";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/customs/modal";
import { Textarea } from "@/components/ui/textarea";
import {
  GetAllExpeditionBoResponseDtoExpedition,
  GetOneExpeditionBoResponseDto,
} from "@/types/api/api-types";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { getExpeditionIdentifier } from "@/lib/expedition.util";

type Props = {
  open: boolean;
  onClose: () => void;
  expedition: GetAllExpeditionBoResponseDtoExpedition | GetOneExpeditionBoResponseDto;
  bypassNote?: boolean;
};

export const DeleteBoModal = ({ open, onClose, expedition, bypassNote }: Props) => {
  const { t } = useTranslation(["shared", "common"]);
  const [note, setNote] = useState<string | undefined>(undefined);
  const { mutateAsync: deleteExpedition, isPending: isDeletionPending } = useDeleteExpeditionBo();
  const { mutateAsync: createNote, isPending: isCreateNotePending } = useCreateNoteBo();

  const isPending = isDeletionPending || isCreateNotePending;

  const identifier = getExpeditionIdentifier(expedition);

  const handleDelete = async () => {
    if (!note && !bypassNote) return;
    if (note) await createNote({ id: expedition.id, payload: note });
    await deleteExpedition(expedition.id);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("deleteExpeditionModal.title", { identifier })}
      description={t("deleteExpeditionModal.description", { identifier })}
      footer={
        <div className="flex w-full flex-row justify-between gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">
            {t("common:actions.cancel")}
          </Button>
          <Button
            onClick={handleDelete}
            disabled={isPending || (!note && !bypassNote)}
            className="flex-1"
          >
            {isPending ? <Loader2 className="animate-spin" /> : t("common:actions.confirm")}
          </Button>
        </div>
      }
    >
      <Textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={
          bypassNote
            ? t("deleteExpeditionModal.reasonPlaceholderOptional")
            : t("deleteExpeditionModal.reasonPlaceholderRequired")
        }
      />
    </Modal>
  );
};
